/**
 * La guida, letta al banco.
 *
 * Due stati e non una pagina sola che scorre: **indice** e **pagina**. Chi legge
 * è in piedi, con un dito guantato, e cerca una cosa sola — scorrere un muro di
 * testo per trovare la riga giusta è il modo di non aprirla mai. L'indice è una
 * griglia di tasti grandi, come i tasti taglia della ricezione: si preme, non si
 * mira.
 *
 * La pagina aperta sta in `useState` e non nell'indirizzo, al contrario
 * dell'ufficio. Aggiungere un `validateSearch` alla radice di questa
 * applicazione vorrebbe dire darlo a **tutte** le sue rotte, comprese le due
 * pubbliche del QR, per una schermata sola. E una guida letta in piedi non si
 * manda a nessuno per link: la si apre e la si legge.
 *
 * Il banco vede solo le pagine del banco. Le sei d'ufficio non si aprono qui, e
 * non è una restrizione di permessi: è che a questa postazione non servono.
 */

import { pagesFor, type GuidePage, type GuideSection } from '@errebi/guide';
import { useSessione } from '@errebi/sessione';
import { useState } from 'react';

import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Spazio, Testata, Voce } from '../components/header';
import { Stato } from '../components/state';
import { FIGURE } from './guide-figures';

export function Guida() {
  const { identita } = useSessione();
  const pagine = pagesFor('banco', identita?.utente.ruoli ?? []);
  const [aperta, setAperta] = useState<string | null>(null);

  const pagina = pagine.find((p) => p.k === aperta) ?? null;

  return (
    <section className="righe guida">
      <Testata>
        <Voce k="Guida" v={pagina ? pagina.title : `${pagine.length} pagine`} />
        {/* La via d'uscita sta accanto al titolo, dove si guarda per sapere dove
            si è: una pagina senza ritorno è un vicolo cieco. */}
        {pagina && (
          <button className="mod" onClick={() => setAperta(null)}>
            Indice
          </button>
        )}
        <Spazio />
        {/* La campana la rende ogni schermata, non il guscio: senza questa riga
            sparirebbe solo qui, e nessuno saprebbe perché. */}
        <Notifiche />
      </Testata>

      {pagine.length === 0 ? (
        <Stato quale="vuoto" dice="Non c’è ancora una guida per il tuo ruolo." />
      ) : pagina === null ? (
        <Indice pagine={pagine} apri={setAperta} />
      ) : (
        <Pagina pagina={pagina} />
      )}
    </section>
  );
}

function Indice({
  pagine,
  apri,
}: {
  readonly pagine: readonly GuidePage[];
  readonly apri: (k: string) => void;
}) {
  return (
    <div className="guida__indice">
      {pagine.map((p) => (
        <button key={p.k} className="guida__voce" onClick={() => apri(p.k)}>
          <span className="guida__voce-t">{p.title}</span>
          <span className="guida__voce-s">{p.summary}</span>
        </button>
      ))}
    </div>
  );
}

function Pagina({ pagina }: { readonly pagina: GuidePage }) {
  return (
    <div className="guida__pagina">
      <p className="guida__sommario">{pagina.summary}</p>
      {pagina.sections.map((s) => (
        <Sezione key={s.title} titolo={s.title}>
          <Corpo sezione={s} />
        </Sezione>
      ))}
    </div>
  );
}

function Corpo({ sezione }: { readonly sezione: GuideSection }) {
  const figura = sezione.figure;
  const src = figura ? FIGURE[figura.file] : undefined;

  return (
    <div className="guida__corpo">
      {sezione.body?.map((p) => (
        <p key={p} className="guida__p">
          <Grassetti testo={p} />
        </p>
      ))}

      {sezione.steps && (
        <ol className="guida__passi">
          {sezione.steps.map((s) => (
            <li key={s.do}>
              <b>{s.do}</b>
              {s.then && ` ${s.then}`}
            </li>
          ))}
        </ol>
      )}

      {/* Solo se importata davvero: un `src` indefinito darebbe un'immagine
          rotta, che è peggio di nessuna immagine. */}
      {figura && src !== undefined && (
        <img
          className="guida__foto"
          src={src}
          alt={figura.alt}
          width={figura.width}
          height={figura.height}
        />
      )}

      {sezione.refusals && (
        <dl className="guida__no">
          {sezione.refusals.map((r) => (
            <div key={`${r.rule}|${r.says}`} className="guida__no-riga">
              <dt>
                <span className="guida__regola">{r.rule}</span> {r.says}
              </dt>
              <dd>
                {r.why} <b>{r.fix}</b>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * Il grassetto dentro un paragrafo, scritto `**così**`.
 *
 * L'unico segno di formattazione ammesso nel testo, e serviva: le pagine
 * elencano coppie «nome — spiegazione», e senza un peso sul nome diventano muri
 * di prosa. Un renderer Markdown per questo sarebbe una quinta dipendenza nel
 * bundle del banco.
 */
function Grassetti({ testo }: { readonly testo: string }) {
  const pezzi = testo.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {pezzi.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          // La chiave è l'indice, ed è il caso in cui va bene: i pezzi non hanno
          // identità propria, la loro identità **è** la posizione nella frase, e
          // una frase non si riordina.
          <b key={i}>{p.slice(2, -2)}</b>
        ) : (
          p
        ),
      )}
    </>
  );
}
